/**
 * Mirrors `next.config.mjs`'s `trailingSlash` setting (enabled on every build
 * except local development). Next's Pages Router used to re-write client-side
 * navigations to the trailing-slash URL shape on these builds; React Router
 * does not, so we re-apply it here to keep deployed URLs (and `asPath`) stable.
 */
export const TRAILING_SLASH_ENABLED =
  process.env['NEXT_PUBLIC_ENV'] !== 'development';

/**
 * Matches absolute (`https://…`, `mailto:…`) and protocol-relative (`//cdn…`)
 * URLs. React Router renders these as plain anchors and a full-page navigation
 * must preserve their exact shape, so trailing-slash normalization is skipped.
 */
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:|^\/\//iu;

/**
 * Appends a trailing slash to a bare pathname. Idempotent (skips paths that
 * already end in `/`) so it is safe to apply to the static-export entry URL,
 * which already carries the slash. The empty-string guard leaves relative
 * navigations (e.g. `?query`-only or `#hash`-only targets) untouched.
 */
export const addTrailingSlashToPathname = (pathname: string): string => {
  if (!TRAILING_SLASH_ENABLED || pathname === '' || pathname.endsWith('/')) {
    return pathname;
  }
  return `${pathname}/`;
};

/**
 * Appends the trailing slash to a full href string. The slash belongs on the
 * path segment only, so the query (`?…`) and hash (`#…`) are split off first
 * and re-attached afterwards — otherwise we'd corrupt URLs like
 * `/waiting-room?slug=x` into `/waiting-room?slug=x/`.
 */
export const addTrailingSlashToHref = (url: string): string => {
  if (!TRAILING_SLASH_ENABLED) {
    return url;
  }
  const hashIndex = url.indexOf('#');
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const queryIndex = beforeHash.indexOf('?');
  const search = queryIndex === -1 ? '' : beforeHash.slice(queryIndex);
  const pathname =
    queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  return `${addTrailingSlashToPathname(pathname)}${search}${hash}`;
};

/**
 * True when `href` is an in-app target React Router navigates client-side.
 * Absolute / protocol-relative URLs are excluded so external links keep their
 * exact shape.
 */
export const isInternalHref = (href: string): boolean =>
  !ABSOLUTE_URL_PATTERN.test(href);

/**
 * Applies the env-appropriate trailing slash to an internal href, leaving
 * absolute/external URLs untouched. Mirrors the normalization `useAppRouter`'s
 * programmatic `push`/`replace` apply, so declarative `<AppLink>` navigation
 * lands on the same URL shape.
 */
export const addTrailingSlashToInternalHref = (href: string): string =>
  isInternalHref(href) ? addTrailingSlashToHref(href) : href;
